/**
 * The headline price for every tier a card has sales in, precomputed.
 *
 * The collection grid needs one number per card, and it used to derive that
 * number on the phone from the full sales record - every listing, for every
 * card on screen. Evidence is 88% of the weight of the price table, so a grid
 * of a hundred cards pulled most of a megabyte to draw a hundred badges, and a
 * shop with ten thousand cards could never have loaded at all.
 *
 * So the number is computed once, when the sales are written, and stored beside
 * them. The tier keys are the ones `tierOf` produces, which is what makes a
 * lookup by the card's own grade possible:
 *
 *   { "NEAR_MINT": 4.12, "LIGHTLY_PLAYED": 3.10, "PSA_10": 120 }
 *
 * Held-out sales are deliberately not applied. A report or an exclude belongs
 * to one viewer, this row is shared by everyone, and the grid has always shown
 * the unheld number. The card's own screen still applies them at read time.
 */

import { tierOf, headlinePrice } from './priceMath';

/** Raw condition tiers, in the order a ladder shows them. */
export const RAW_TIERS = Object.freeze({
  nm: 'NEAR_MINT',
  lp: 'LIGHTLY_PLAYED',
  mp: 'MODERATELY_PLAYED',
  hp: 'HEAVILY_PLAYED',
  dmg: 'DAMAGED',
});

/** The vendor's own average for one raw condition, if it has one. */
export function tierAverage(stats, shortKey) {
  const LONG = {
    nm: 'Near Mint', lp: 'Lightly Played', mp: 'Moderately Played',
    hp: 'Heavily Played', dmg: 'Damaged',
  };
  const row = stats?.[LONG[shortKey]] ?? stats?.[shortKey];
  const v = row?.med ?? row?.avg;
  return typeof v === 'number' && v > 0 ? Math.round(v * 100) / 100 : null;
}

export function computeHeadlineMap(evidence, asks, stats) {
  const map = {};

  // Only tiers that actually have sales. Nothing else can produce a headline.
  const tiers = new Set();
  for (const sale of evidence || []) {
    const key = tierOf(sale);
    if (key) tiers.add(key);
  }

  for (const key of tiers) {
    const priced = headlinePrice(evidence, key, null);
    if (priced?.value != null) map[key] = priced.value;
  }

  // Where no sale backs a raw condition, the vendor's own average for that
  // condition stands in, and failing that the TCGplayer ask.
  //
  // Raw only, and deliberately so. The $13,600 Fossil Dragonite came from a
  // *graded* card reading an aggregate that was not its grade: the app held
  // zero PSA 10 sales for it and the highest sale on the whole card was a CGC
  // 10 at $975. A raw condition average is the right card in the right
  // condition, which is a different thing, and refusing it left cards reading
  // "no price" on screen while the breakdown behind them showed $0.11.
  for (const [shortKey, tierKey] of Object.entries(RAW_TIERS)) {
    if (map[tierKey] != null) continue;
    const avg = tierAverage(stats, shortKey);
    if (avg != null) { map[tierKey] = avg; continue; }
    const ask = asks?.[shortKey]?.price;
    if (typeof ask === 'number' && ask > 0) map[tierKey] = ask;
  }

  return map;
}

/**
 * The stored number for one card, by the grade that card is in.
 * `grade` is a raw condition short key ("nm") or a slab ("PSA 10").
 */
export function headlineFor(headline, grade, gradeKeyOf, conditionKeyOf, graded) {
  if (!headline) return null;
  const key = graded
    ? gradeKeyOf(grade)
    : RAW_TIERS[conditionKeyOf(grade)];
  const value = key ? headline[key] : null;
  return typeof value === 'number' && value > 0 ? value : null;
}

/** The raw tiers from best to worst, which is the axis an estimate runs along. */
const RAW_ORDER = Object.values(RAW_TIERS);

/**
 * A number for a raw condition nothing has priced, from the conditions that
 * were.
 *
 * The pricing screen has to have an answer. When a vendor has near mint and
 * heavily played sales and nothing in between, the honest answer for a
 * moderately played copy is between the two, not a blank. So a missing tier is
 * read off its neighbours: halfway between the nearest priced tier above and
 * below, weighted by how far away each is; or, with a neighbour on only one
 * side, that neighbour's number.
 *
 * Raw only. A slab's grade is not a point on this line, and the $13,600 Fossil
 * Dragonite was a graded card reading a number that was not its grade.
 *
 * @param {Object} byTier  { NEAR_MINT: 4.1, HEAVILY_PLAYED: 1.5, ... } real numbers only
 * @param {string} tierKey  the tier wanted, e.g. "MODERATELY_PLAYED"
 * @returns {{ value: number, from: string[] } | null}
 */
export function estimateRawTier(byTier, tierKey) {
  const at = RAW_ORDER.indexOf(tierKey);
  if (at < 0 || !byTier) return null;
  const has = (k) => typeof byTier[k] === 'number' && byTier[k] > 0;

  let above = null;
  for (let i = at - 1; i >= 0; i -= 1) if (has(RAW_ORDER[i])) { above = i; break; }
  let below = null;
  for (let i = at + 1; i < RAW_ORDER.length; i += 1) if (has(RAW_ORDER[i])) { below = i; break; }

  if (above == null && below == null) return null;
  const round = (v) => Math.round(v * 100) / 100;

  if (above != null && below != null) {
    const a = byTier[RAW_ORDER[above]];
    const b = byTier[RAW_ORDER[below]];
    const span = below - above;
    const t = (at - above) / span;
    return { value: round(a + (b - a) * t), from: [RAW_ORDER[above], RAW_ORDER[below]] };
  }
  const only = RAW_ORDER[above != null ? above : below];
  return { value: round(byTier[only]), from: [only] };
}

/**
 * The stored number for a grade, estimated from the neighbouring conditions
 * when there is none. Says which it was.
 *
 * @returns {{ value: number, estimated: boolean, from?: string[] } | null}
 */
export function headlineOrEstimate(headline, grade, gradeKeyOf, conditionKeyOf, graded) {
  const own = headlineFor(headline, grade, gradeKeyOf, conditionKeyOf, graded);
  if (own != null) return { value: own, estimated: false };
  if (graded || !headline) return null;
  const est = estimateRawTier(headline, RAW_TIERS[conditionKeyOf(grade)]);
  return est ? { value: est.value, estimated: true, from: est.from } : null;
}
